import { Box } from "@mui/material";
import type { PropsWithChildren, ReactNode } from "react";
import { useCallback, useRef, useState } from "react";
import { useDrag, useDrop } from "react-dnd";

interface DraggedRow {
  id: string;
}

const countRowsAbovePointer = (rows: HTMLCollection, draggedId: string, pointerY: number) =>
  [...rows].filter((row) => {
    if (!(row instanceof HTMLElement) || row.dataset["sortableId"] === draggedId) {
      return false;
    }
    const { top, height } = row.getBoundingClientRect();
    return top + height / 2 < pointerY;
  }).length;

interface SortableRowProps {
  id: string;
  type: string;
  isOutsideDropArea: boolean;
  onDragEnd: (dropped: boolean) => void;
}

const SortableRow = ({ id, type, isOutsideDropArea, onDragEnd, children }: PropsWithChildren<SortableRowProps>) => {
  const [{ isDragging }, drag] = useDrag(
    () => ({
      type,
      item: { id },
      isDragging: (monitor) => monitor.getItem<DraggedRow>().id === id,
      collect: (monitor) => ({ isDragging: monitor.isDragging() }),
      end: (_, monitor) => onDragEnd(monitor.didDrop()),
    }),
    [type, id, onDragEnd]
  );

  const connectRow = useCallback(
    (node: HTMLDivElement | null) => {
      drag(node);
    },
    [drag]
  );

  return (
    <Box
      ref={connectRow}
      data-sortable-id={id}
      sx={[
        { cursor: "grab", borderRadius: 1, bgcolor: "background.paper" },
        isDragging && { "& > *": { opacity: 0.4 } },
        isDragging &&
          !isOutsideDropArea &&
          ((theme) => ({
            outline: `1px dashed ${theme.palette.primary.main}`,
            outlineOffset: -1,
            bgcolor: theme.alpha(theme.palette.primary.main, 0.08),
          })),
      ]}
    >
      {children}
    </Box>
  );
};

interface Props<T> {
  type: string;
  items: T[];
  getItemId: (item: T) => string;
  renderItem: (item: T) => ReactNode;
  onReorder: (items: T[]) => void;
  dropArea?: HTMLElement | null;
}

const SortableList = <T,>({ type, items, getItemId, renderItem, onReorder, dropArea }: Props<T>) => {
  const [dragStartItems, setDragStartItems] = useState<T[]>();
  const listRef = useRef<HTMLDivElement | null>(null);

  const [{ isOver }, drop] = useDrop<DraggedRow, void, { isOver: boolean }>({
    accept: type,
    hover: (dragged, monitor) => {
      const pointer = monitor.getClientOffset();
      const rows = listRef.current?.children;
      const draggedItem = items.find((item) => getItemId(item) === dragged.id);
      if (!pointer || !rows || draggedItem === undefined) {
        return;
      }
      if (dragStartItems === undefined) {
        setDragStartItems(items);
      }

      const insertIndex = countRowsAbovePointer(rows, dragged.id, pointer.y);
      const others = items.filter((item) => item !== draggedItem);
      const reordered = [...others.slice(0, insertIndex), draggedItem, ...others.slice(insertIndex)];
      if (reordered.some((item, index) => item !== items[index])) {
        onReorder(reordered);
      }
    },
    collect: (monitor) => ({ isOver: monitor.isOver() }),
  });

  const connectList = useCallback(
    (node: HTMLDivElement | null) => {
      listRef.current = node;
      drop(node && (dropArea ?? node));
    },
    [drop, dropArea]
  );

  const handleDragEnd = (dropped: boolean) => {
    if (!dropped && dragStartItems) {
      onReorder(dragStartItems);
    }
    setDragStartItems(undefined);
  };

  const isOutsideDropArea = dragStartItems !== undefined && !isOver;
  const displayedItems = isOutsideDropArea ? dragStartItems : items;

  return (
    <Box ref={connectList}>
      {displayedItems.map((item) => {
        const id = getItemId(item);
        return (
          <SortableRow key={id} id={id} type={type} isOutsideDropArea={isOutsideDropArea} onDragEnd={handleDragEnd}>
            {renderItem(item)}
          </SortableRow>
        );
      })}
    </Box>
  );
};

export default SortableList;
